/**
 * EngagementSettingsModal — staff-facing per-client engagement
 * configuration: engagement type, financial-reporting framework,
 * disclosures omitted, independence (compilation only), Description
 * of Basis page toggle + custom text.
 *
 * Added 2026-05-21 push (Items 3 + 7) in response to peer-review
 * of V10 / Compilation V2:
 *   • Engagement type and framework are now per-client, not
 *     hard-coded.
 *   • All downstream consumers (statement titles, footer wording,
 *     compilation report paragraphs, Description of Basis page)
 *     read from these fields.
 *
 * Design notes:
 *   - "Accrual" is NOT a framework option. Accrual is a method
 *     implicit in GAAP. The five options are GAAP, Income Tax
 *     Basis, Cash Basis, Modified Cash Basis, Other.
 *   - When framework=Other, a free-text label flows into titles
 *     and the compilation report's framework phrase.
 *   - Independence field is shown ONLY for compilation engagements
 *     — AR-C 70 (preparation) does not require an independence
 *     statement.
 *   - "Include Description of Basis page" is locked ON when
 *     engagement=compilation AND framework != GAAP (required by
 *     AR-C 80.A24-A26); locked OFF and irrelevant for GAAP
 *     engagements; toggleable for prepared+non-GAAP.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Settings, X, Info } from 'lucide-react';

const COLORS = {
  primary: '#0085CA',
  border: '#E5E7EB',
  muted: '#6B7280',
  danger: '#DC2626',
  bg: '#F9FAFB',
};

const FRAMEWORK_OPTIONS = [
  { value: 'gaap', label: 'GAAP — Generally Accepted Accounting Principles' },
  { value: 'income_tax_basis', label: 'Income Tax Basis' },
  { value: 'cash_basis', label: 'Cash Basis' },
  { value: 'modified_cash_basis', label: 'Modified Cash Basis' },
  { value: 'other', label: 'Other (specify below)' },
  // V18 — AR-C 70 .A6 path: a PREPARED engagement may omit identification
  // of an applicable financial reporting framework if specific disclosure
  // language is included on each statement. Hidden when engagement type
  // is COMPILATION (AR-C 80 requires a framework reference).
  { value: 'none', label: 'None — No Framework Applied (AR-C 70 only)', preparationOnly: true },
];

// V18 — AR-C 70.A6 disclosure language rendered on every statement page
// AND on the cover page when framework='none'. Verbatim from the standard.
const NONE_FRAMEWORK_DISCLOSURE =
  'No assurance is provided on these financial statements. The financial statements have not been prepared in accordance with an applicable financial reporting framework, and accordingly may not be appropriate for users who are not informed about the basis of accounting used.';

const ENGAGEMENT_OPTIONS = [
  { value: 'preparation', label: 'Prepared (AR-C 70)' },
  { value: 'compilation', label: 'Compilation (AR-C 80)' },
];

const ENTITY_TYPE_OPTIONS = [
  { value: 'sole_prop', label: 'Sole Proprietor' },
  { value: 'smllc', label: 'Single-Member LLC' },
  { value: 'multi_llc', label: 'Multi-Member LLC' },
  { value: 'llc', label: 'LLC' },
  { value: 's_corp', label: 'S-Corporation' },
  { value: 'c_corp', label: 'C-Corporation' },
  { value: 'nonprofit', label: 'Not-for-Profit' },
];

// Defaults per framework — match the renderer's fallback so staff see
// the same default in the editor that will print if they don't change
// it. Keep in sync with PrintModal renderDescriptionOfBasis() defaults.
const DESCRIPTION_OF_BASIS_DEFAULTS = {
  income_tax_basis:
    'These financial statements have been prepared on the accounting basis used by the Company for federal income tax purposes, which is a comprehensive basis of accounting other than accounting principles generally accepted in the United States of America. Under this basis, revenue is recognized when earned for tax purposes and expenses are recognized when deductible under the Internal Revenue Code, which may differ from the timing of recognition under accounting principles generally accepted in the United States of America. Depreciation, amortization, and other allowances follow the methods elected by the Company on its federal income tax returns.',
  cash_basis:
    'These financial statements have been prepared on the cash basis of accounting. Under this basis, revenue is recognized when received and expenses are recognized when paid. This basis of accounting differs from accounting principles generally accepted in the United States of America primarily because revenue is not recognized as earned and expenses are not recognized as incurred; accounts receivable, accounts payable, accrued liabilities, and prepaid expenses are not recorded.',
  modified_cash_basis:
    'These financial statements have been prepared on the modified cash basis of accounting. Under this basis, revenue is recognized when received and expenses are recognized when paid, except that certain assets are capitalized and depreciated and certain liabilities are recorded as incurred. This basis differs from accounting principles generally accepted in the United States of America primarily in the treatment of accounts receivable, accounts payable, and accrued liabilities.',
  other:
    'These financial statements have been prepared on a basis of accounting other than accounting principles generally accepted in the United States of America. The specific basis of accounting is described in the terms of the engagement between the Company and Steele Martin Jones & Co., PLC.',
};

export default function EngagementSettingsModal({ client, onClose, onSaved, totalAssets = 0 }) {
  const [engagementType, setEngagementType] = useState(client.engagement_type || 'preparation');
  const [entityType, setEntityType] = useState(client.entity_type || '');
  const [framework, setFramework] = useState(client.framework || 'gaap');
  const [frameworkOtherLabel, setFrameworkOtherLabel] = useState(client.framework_other_label || '');
  const [disclosuresOmitted, setDisclosuresOmitted] = useState(client.disclosures_omitted !== false);
  const [independent, setIndependent] = useState(client.independent !== false);
  const [includeDescPage, setIncludeDescPage] = useState(client.include_description_of_basis === true);
  const [descText, setDescText] = useState(client.description_of_basis_text || '');
  // V17 — BS PY comparison basis.
  const [bsPyBasis, setBsPyBasis] = useState(client.bs_py_comparison_basis || 'prior_year_same_date');
  // V25.2 — accounting basis used for EVERY QuickBooks pull for this client.
  const [reportingBasis, setReportingBasis] = useState(client.reporting_basis || 'accrual');
  const [basisDiag, setBasisDiag] = useState(null);
  const [basisDiagLoading, setBasisDiagLoading] = useState(false);

  // V25.2 — pulls the last 4 months on BOTH bases so staff can see, rather
  // than guess, why a month looks empty or a total does not tie.
  // V25.5 — raw evidence for "that month isn't pulling": what QuickBooks
  // actually returned per monthly column, on both bases.
  const runColumnProbe = async () => {
    setBasisDiagLoading(true);
    setBasisDiag(null);
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/qbo/ttm-column-probe/${client.id}?months=12`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('smj_token')}` },
          timeout: 180000 },
      );
      setBasisDiag({ probe: resp.data });
    } catch (err) {
      setBasisDiag({ error: err?.response?.data?.detail || err?.message || 'Probe failed.' });
    } finally {
      setBasisDiagLoading(false);
    }
  };

  const runBasisDiagnostic = async () => {
    setBasisDiagLoading(true);
    setBasisDiag(null);
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/qbo/basis-diagnostic/${client.id}?months=4`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('smj_token')}` },
          timeout: 120000 },
      );
      setBasisDiag(resp.data);
    } catch (err) {
      setBasisDiag({ error: err?.response?.data?.detail || err?.message || 'Diagnostic failed.' });
    } finally {
      setBasisDiagLoading(false);
    }
  };

  // V18 — when staff toggles engagement type to compilation while
  // framework is "none", auto-revert the framework to GAAP. AR-C 80
  // requires a framework reference; "None" is invalid for compilation.
  useEffect(() => {
    if (engagementType === 'compilation' && framework === 'none') {
      setFramework('gaap');
    }
  }, [engagementType, framework]);
  // V15: per-engagement Cash Flow materiality threshold. Stored as a
  // string in the input so the user can clear the field back to auto.
  const [cfThresholdInput, setCfThresholdInput] = useState(
    client.cf_materiality_threshold == null ? '' : String(client.cf_materiality_threshold)
  );
  const cfThresholdAuto = Math.max(1000, 0.005 * Math.abs(totalAssets || 0));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('smj_token');

  // Item 7 lock logic: compilation + non-GAAP forces Description-of-Basis ON.
  const descLockedOn = engagementType === 'compilation' && framework !== 'gaap';
  // For GAAP engagements, the page is irrelevant — keep it OFF.
  const descLockedOff = framework === 'gaap';
  const effectiveDescPage = descLockedOn ? true : (descLockedOff ? false : includeDescPage);

  // Auto-fill description text with the framework default when the user
  // hasn't customized it AND we're on a framework that has a default.
  useEffect(() => {
    if (!descText && DESCRIPTION_OF_BASIS_DEFAULTS[framework]) {
      setDescText(DESCRIPTION_OF_BASIS_DEFAULTS[framework]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [framework]);

  const save = async () => {
    setError('');
    if (framework === 'other' && !frameworkOtherLabel.trim()) {
      setError('Please enter a label for the "Other" framework (e.g. "Modified Tax Basis").');
      return;
    }
    // V21 (2026-06-11) — Compilation framework guard. AR-C 80 requires
    // the compilation report to reference an applicable financial
    // reporting framework; framework='none' is the AR-C 70 .A6 path
    // for preparation engagements only. Block save here so the
    // misconfigured state never reaches the database (server-side
    // enforces the same check; this UI-side error message keeps the
    // user from a round-trip).
    if (engagementType === 'compilation' && (!framework || framework === 'none')) {
      setError(
        'Compilation engagements require a framework. Choose GAAP, ' +
        'Income Tax Basis, Cash Basis, Modified Cash Basis, or Other. ' +
        'AR-C 80 requires the compilation report to reference an ' +
        'applicable financial reporting framework.'
      );
      return;
    }
    setSaving(true);
    try {
      // CF materiality threshold validation
      let cfMatThreshold = null;
      const trimmed = cfThresholdInput.trim();
      if (trimmed !== '') {
        const n = Number(trimmed.replace(/[$,]/g, ''));
        if (!Number.isFinite(n) || n < 0) {
          setError('Cash Flow materiality threshold must be a non-negative number, or blank for auto.');
          setSaving(false);
          return;
        }
        cfMatThreshold = n;
      }
      const payload = {
        engagement_type: engagementType,
        // only written when the staff member actually chose one, so a legacy
        // client with no entity type is not silently assigned one on save
        ...(entityType ? { entity_type: entityType } : {}),
        framework,
        framework_other_label: framework === 'other' ? frameworkOtherLabel.trim() : null,
        disclosures_omitted: disclosuresOmitted,
        // AR-C 70 (preparation) has no concept of accountant independence
        // in the way AR-C 80 does. We still store the field for audit
        // trail, but for prepared engagements we leave it at True.
        independent: engagementType === 'compilation' ? independent : true,
        include_description_of_basis: effectiveDescPage,
        description_of_basis_text: effectiveDescPage ? (descText.trim() || null) : null,
        cf_materiality_threshold: cfMatThreshold,
        bs_py_comparison_basis: bsPyBasis,
        reporting_basis: reportingBasis,
      };
      const res = await fetch(`${BACKEND_URL}/api/clients/${client.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        throw new Error(detail.detail || `HTTP ${res.status}`);
      }
      if (onSaved) onSaved({ ...client, ...payload });
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="engagement-settings-modal"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 8, padding: 24,
          maxWidth: 640, width: '100%', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', gap: 16, overflow: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 18 }}>
            <Settings size={18} color={COLORS.primary} /> Engagement Settings
          </h3>
          <button
            data-testid="engagement-settings-close"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 }}
          >
            <X size={18} color={COLORS.muted} />
          </button>
        </div>

        <div style={{ fontSize: 13, color: COLORS.muted }}>
          {client.company} · changes apply to all future print runs and snapshots for this engagement.
        </div>

        {/* Engagement Type */}
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Engagement Type
          </label>
          <select
            data-testid="engagement-type-select"
            value={engagementType}
            onChange={(e) => setEngagementType(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 14 }}
          >
            {ENGAGEMENT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        </div>

        {/* Entity Type — V25.19: drives the statement terminology. A
            not-for-profit prints Net Assets / Support and Revenue /
            Change in Net Assets and the NFP statement titles. */}
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Entity Type
          </label>
          <select
            data-testid="entity-type-select"
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 14 }}
          >
            {!entityType && <option value="">— Not set —</option>}
            {ENTITY_TYPE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          {entityType === 'nonprofit' && (
            <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 6 }}>
              Statements will read Statement of Financial Position, Statement of
              Activities, Net Assets, Support and Revenue, and Change in Net Assets.
            </div>
          )}
        </div>

        {/* Framework */}
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Financial Reporting Framework
          </label>
          <select
            data-testid="framework-select"
            value={framework}
            onChange={(e) => setFramework(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 14 }}
          >
            {FRAMEWORK_OPTIONS
              .filter(opt => !(opt.preparationOnly && engagementType !== 'preparation'))
              .map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          {framework === 'other' && (
            <input
              data-testid="framework-other-input"
              type="text"
              placeholder='Framework label (e.g. "Statutory Basis", "Regulatory Basis")'
              value={frameworkOtherLabel}
              onChange={(e) => setFrameworkOtherLabel(e.target.value)}
              style={{ width: '100%', marginTop: 8, padding: '8px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 14 }}
            />
          )}
          {engagementType === 'preparation' && (
            <div
              data-testid="framework-prepared-note"
              style={{
                marginTop: 8,
                padding: '8px 10px',
                background: '#F3F4F6',
                borderRadius: 6,
                fontSize: 11,
                lineHeight: 1.5,
                color: COLORS.muted,
                fontStyle: 'italic',
              }}
            >
              Framework selection is recorded but is not disclosed on
              prepared engagement output. All prepared packages render
              the AR-C 70.A6 disclosure regardless of this value.
            </div>
          )}
        </div>

        {/* Disclosures Omitted */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <input
            data-testid="disclosures-omitted-checkbox"
            id="disclosures-omitted"
            type="checkbox"
            checked={disclosuresOmitted}
            onChange={(e) => setDisclosuresOmitted(e.target.checked)}
          />
          <label htmlFor="disclosures-omitted" style={{ fontSize: 14, cursor: 'pointer' }}>
            Substantially all disclosures are omitted
          </label>
        </div>

        {/* Cash Flow Materiality Threshold (V15) */}
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Cash Flow Materiality Threshold ($)
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              data-testid="cf-materiality-input"
              type="text"
              inputMode="decimal"
              placeholder={`Auto: ${cfThresholdAuto.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
              value={cfThresholdInput}
              onChange={(e) => setCfThresholdInput(e.target.value)}
              style={{ flex: 1, padding: '8px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 14 }}
            />
            <button
              type="button"
              data-testid="cf-materiality-reset"
              onClick={() => setCfThresholdInput('')}
              style={{ padding: '8px 12px', background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 12, cursor: 'pointer', color: COLORS.muted }}
            >Use auto</button>
          </div>
          <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 6, display: 'flex', alignItems: 'flex-start', gap: 4 }}>
            <Info size={12} style={{ marginTop: 2 }} />
            <span>
              Below-threshold operating, investing, and financing items are
              rolled into per-section &quot;Other changes in …&quot; lines so
              totals still tie to BS deltas exactly. Default formula
              <strong> max($1,000, 0.5% of total assets)</strong> applied
              when blank. Depreciation, amortization, and supplemental
              disclosures (interest, taxes paid) are never thresholded.
            </span>
          </div>
        </div>

        {/* V25.2 — QuickBooks accounting basis. This changes the NUMBERS,
            not the presentation: a cash-basis engagement pulled on Accrual
            reports billings instead of collections. */}
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            QuickBooks Accounting Basis
          </label>
          <select
            data-testid="reporting-basis-select"
            value={reportingBasis}
            onChange={(e) => setReportingBasis(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 14 }}
          >
            <option value="accrual">Accrual — revenue when billed</option>
            <option value="cash">Cash — revenue when collected</option>
          </select>
          <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 6, display: 'flex', alignItems: 'flex-start', gap: 4 }}>
            <Info size={12} style={{ marginTop: 2 }} />
            <span>
              Sent to QuickBooks as <code>accounting_method</code> on every
              Trial Balance, P&amp;L and trailing-12-months pull, and it is part
              of the data cache key. Set this to <strong>Cash</strong> for any
              engagement reported on collections — on Accrual those clients
              show billed revenue and phantom payables.
            </span>
          </div>
        </div>

          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              data-testid="basis-diagnostic-btn"
              onClick={runBasisDiagnostic}
              disabled={basisDiagLoading}
              style={{ padding: '7px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                       border: `1px solid ${COLORS.border}`, background: '#fff',
                       color: COLORS.dark, cursor: basisDiagLoading ? 'wait' : 'pointer' }}
            >
              {basisDiagLoading ? 'Pulling both bases\u2026' : 'Compare Cash vs Accrual (last 4 months)'}
            </button>
            <button
              type="button"
              data-testid="column-probe-btn"
              onClick={runColumnProbe}
              disabled={basisDiagLoading}
              style={{ marginLeft: 8, padding: '7px 12px', borderRadius: 6, fontSize: 12,
                       fontWeight: 600, border: `1px solid ${COLORS.border}`, background: '#fff',
                       color: COLORS.dark, cursor: basisDiagLoading ? 'wait' : 'pointer' }}
            >
              Why is a month empty? (12-month column probe)
            </button>
            {basisDiag && (
              <pre
                data-testid="basis-diagnostic-output"
                style={{ marginTop: 10, whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, monospace',
                         fontSize: 11, lineHeight: 1.55, background: '#F7FAFC',
                         border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: 10,
                         maxHeight: 340, overflowY: 'auto' }}
              >
                {basisDiag.error
                  ? `Diagnostic failed: ${typeof basisDiag.error === 'string' ? basisDiag.error : JSON.stringify(basisDiag.error)}`
                  : basisDiag.probe
                  ? ['accrual', 'cash'].map((b) => {
                      const p = basisDiag.probe[b] || {};
                      if (p.error) return `${b.toUpperCase()}: ${p.error}`;
                      const head = `${b.toUpperCase()}  (QuickBooks reports basis: ${p.report_basis || '?'}, `
                        + `${p.columns_returned} columns for ${basisDiag.probe.requested.start} .. ${basisDiag.probe.requested.end})`;
                      const lines = (p.columns || []).map((c) => {
                        const label = (c.title || `${c.start}`).padEnd(12, ' ');
                        return `    ${label} ${String(c.nonzero_rows).padStart(4, ' ')} figures   ${Math.round(c.abs_total).toLocaleString()}`;
                      });
                      return [head, ...lines].join('\n');
                    }).join('\n\n')
                  : [`Configured basis: ${basisDiag.configured_basis}  (setting: ${basisDiag.reporting_basis_setting})`, '']
                    .concat((basisDiag.months || []).map((m) => {
                      const fmt = (o) => (o?.error
                        ? `error: ${o.error}`.slice(0, 60)
                        : `revenue ${Math.round(o?.total_revenue || 0).toLocaleString()}  net ${Math.round(o?.net_income || 0).toLocaleString()}`);
                      return `${m.month}\n    Accrual   ${fmt(m.accrual)}\n    Cash      ${fmt(m.cash)}`;
                    }))
                    .join('\n')}
              </pre>
            )}
          </div>

        {/* V17 — Balance Sheet PY comparison basis */}
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Balance Sheet Prior-Year Comparison Basis
          </label>
          <select
            data-testid="bs-py-basis-select"
            value={bsPyBasis}
            onChange={(e) => setBsPyBasis(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 14 }}
          >
            <option value="prior_year_same_date">
              Same date one year prior (e.g. 4/30/2026 vs 4/30/2025)
            </option>
            <option value="period_start_basis">
              Period-start of current year (e.g. 4/30/2026 vs 12/31/2025) — YTD-comparable
            </option>
          </select>
          <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 6, display: 'flex', alignItems: 'flex-start', gap: 4 }}>
            <Info size={12} style={{ marginTop: 2 }} />
            <span>
              Controls which prior-period balance the BS PY column compares
              against. Threaded into the <code>bs_py_period_matches_engagement</code>
              validation tie. Default <strong>same date one year prior</strong>
              is the conventional CPA presentation; <strong>period-start</strong>
              is sometimes preferred for interim YTD packages.
            </span>
          </div>
        </div>

        {/* Independence — compilation only */}
        {engagementType === 'compilation' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              data-testid="independent-checkbox"
              id="independent"
              type="checkbox"
              checked={independent}
              onChange={(e) => setIndependent(e.target.checked)}
            />
            <label htmlFor="independent" style={{ fontSize: 14, cursor: 'pointer' }}>
              The firm is independent with respect to this client
              <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 2 }}>
                Per AR-C 80.18, an explicit "we are not independent" statement is required when independence is impaired.
              </div>
            </label>
          </div>
        )}

        {/* Description of Basis page toggle + text */}
        <div style={{ padding: 12, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              data-testid="include-desc-checkbox"
              id="include-desc-page"
              type="checkbox"
              checked={effectiveDescPage}
              onChange={(e) => setIncludeDescPage(e.target.checked)}
              disabled={descLockedOn || descLockedOff}
            />
            <label htmlFor="include-desc-page" style={{ fontSize: 14, cursor: descLockedOn || descLockedOff ? 'not-allowed' : 'pointer' }}>
              Include &quot;Description of Basis of Accounting&quot; page
              {descLockedOn && (
                <div style={{ fontSize: 12, color: COLORS.primary, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Info size={12} /> Required for compilation engagements with non-GAAP framework (AR-C 80.A24-A26).
                </div>
              )}
              {descLockedOff && (
                <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 2 }}>
                  Not applicable for GAAP engagements.
                </div>
              )}
            </label>
          </div>
          {effectiveDescPage && (
            <textarea
              data-testid="desc-text-input"
              value={descText}
              onChange={(e) => setDescText(e.target.value)}
              placeholder={DESCRIPTION_OF_BASIS_DEFAULTS[framework] || 'Description of the basis of accounting...'}
              rows={8}
              style={{
                width: '100%', marginTop: 12, padding: 10, border: `1px solid ${COLORS.border}`,
                borderRadius: 6, fontSize: 13, lineHeight: 1.5, fontFamily: 'inherit',
              }}
            />
          )}
        </div>

        {error && (
          <div data-testid="engagement-settings-error" style={{ color: COLORS.danger, fontSize: 13 }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          <button
            data-testid="engagement-settings-cancel"
            onClick={onClose}
            disabled={saving}
            style={{ padding: '8px 16px', background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 14, cursor: 'pointer' }}
          >Cancel</button>
          <button
            data-testid="engagement-settings-save"
            onClick={save}
            disabled={saving}
            style={{ padding: '8px 16px', background: COLORS.primary, color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {saving && <Loader2 size={14} className="animate-spin" />} Save Settings
          </button>
        </div>
      </div>
    </div>
  );
}
